import { StatusConfig } from '@/types/statusConfig';
import { WebQuote, WebQuoteItem } from '@/types/webQuote';
import { computed, ComputedRef, Ref, ref, watch } from 'vue';
import { useUserStore } from './auth';
import { getIdToken } from 'firebase/auth';
import { useFormatters } from '@/util/formatters';
import { useApiEnvStore } from './env';

export const useWebQuoteManager = () => {
	const statusModule = createStatusModule();
	const listModule = createListModule();
	const searchFilterModule = createSearchFilterModule(listModule.webQuotesRaw);
	const paginationModule = createPaginationModule(searchFilterModule.searchedWebQuotes);
	const tableModule = createTableModule(paginationModule.pagedWebQuotes);
	const selectionModule = createSelectionModule(listModule.webQuotesRaw);
	const dataAccessModule = createDataAccessModule(listModule, statusModule);

	return {
		...statusModule,
		...listModule,
		...searchFilterModule,
		...paginationModule,
		...tableModule,
		...selectionModule,
		...dataAccessModule
	};
};

//#region Status
function createStatusModule() {
	const isFetching = ref<boolean>(false);
	const lastUpdateTime = ref<string>('December 18, 2025 02:10:00');
	const webQuoteStatus = ref<StatusConfig>({
		statusSymbol: 'check_circle',
		statusText: 'Idle'
	});

	function resetWebQuoteStatus() {
		webQuoteStatus.value = {
			statusSymbol: 'check_circle',
			statusText: 'Idle'
		};
		isFetching.value = false;
	}

	return {
		isFetching,
		lastUpdateTime,
		webQuoteStatus,
		resetWebQuoteStatus
	};
}
//#endregion

//#region List
function createListModule() {
	const webQuotesRaw: Ref<WebQuote[]> = ref([]);

	return {
		webQuotesRaw
	};
}
//#endregion

//#region Search and Filter
function createSearchFilterModule(webQuotes: Ref<WebQuote[]>) {
	const searchQuery = ref<string>('');

	enum FilterByValue {
		'all',
		'last_1',
		'last_7',
		'last_30'
	}
	const filterBy = ref<FilterByValue>(FilterByValue.all);

	const filteredWebQuotes: ComputedRef<WebQuote[]> = computed(() => {
		const now = Date.now();
		const day = 1000 * 60 * 60 * 24;
		const oneDayAgo = new Date(now - day);
		const sevenDaysAgo = new Date(now - 7 * day);
		const thirtyDaysAgo = new Date(now - 30 * day);

		switch (filterBy.value) {
			case FilterByValue.all:
				return webQuotes.value;
			case FilterByValue.last_1:
				return webQuotes.value.filter((webQuote: WebQuote) => new Date(webQuote.dateCreated) > oneDayAgo);
			case FilterByValue.last_7:
				return webQuotes.value.filter((webQuote: WebQuote) => new Date(webQuote.dateCreated) > sevenDaysAgo);
			case FilterByValue.last_30:
				return webQuotes.value.filter((webQuote: WebQuote) => new Date(webQuote.dateCreated) > thirtyDaysAgo);
			default:
				return webQuotes.value;
		}
	});

	const sortedWebQuotes: ComputedRef<WebQuote[]> = computed(() => {
		return [...filteredWebQuotes.value].sort((a: WebQuote, b: WebQuote) => Number(b.id) - Number(a.id));
	});

	const searchedWebQuotes: ComputedRef<WebQuote[]> = computed(() => {
		if (!searchQuery.value.trim()) return sortedWebQuotes.value;
		const query = searchQuery.value.trim().toLowerCase();
		return sortedWebQuotes.value.filter((webQuote) => {
			const customer = webQuote.customer.toLowerCase();
			const id = String(webQuote.id).toLowerCase();
			const total = webQuote.total.toLocaleString().toLowerCase();
			return customer.includes(query) || id.includes(query) || total.includes(query);
		});
	});

	const filteredWebQuotesCount: ComputedRef<number> = computed(() => searchedWebQuotes.value.length);
	const filteredWebQuotesValue: ComputedRef<number> = computed(() => searchedWebQuotes.value.reduce((sum, webQuote) => sum + webQuote.total, 0));

	function setFilterBy(fb: FilterByValue) {
		filterBy.value = fb;
	}

	return {
		searchQuery,
		FilterByValue,
		filterBy,
		searchedWebQuotes,
		filteredWebQuotesCount,
		filteredWebQuotesValue,
		setFilterBy
	};
}
//#endregion

//#region Pagination
function createPaginationModule(webQuotes: ComputedRef<WebQuote[]>) {
	const pageSize = ref<number>(15);
	const currentPage = ref<number>(1);
	const totalItems = computed<number>(() => webQuotes.value.length);
	const totalPages = computed<number>(() => Math.max(1, Math.ceil(totalItems.value / pageSize.value)));
	const pageStartIndex = computed(() => (currentPage.value - 1) * pageSize.value);
	const pageEndIndex = computed(() => Math.min(pageStartIndex.value + pageSize.value, totalItems.value));
	const pagedWebQuotes = computed(() => webQuotes.value.slice(pageStartIndex.value, pageEndIndex.value));
	const isFirstPage = computed(() => currentPage.value === 1);
	const isLastPage = computed(() => currentPage.value === totalPages.value);
	function nextPage() {
		if (currentPage.value < totalPages.value) {
			currentPage.value++;
		}
	}
	function prevPage() {
		if (currentPage.value > 1) {
			currentPage.value--;
		}
	}
	function firstPage() {
		currentPage.value = 1;
	}
	function lastPage() {
		currentPage.value = totalPages.value;
	}
	watch(totalPages, (newVal) => {
		if (currentPage.value > newVal) {
			currentPage.value = newVal;
		}
	});
	return {
		currentPage,
		totalItems,
		totalPages,
		pageStartIndex,
		pageEndIndex,
		pagedWebQuotes,
		isFirstPage,
		isLastPage,
		nextPage,
		prevPage,
		firstPage,
		lastPage
	};
}
//#endregion

//#region Table
function createTableModule(webQuotesRaw: ComputedRef<WebQuote[]>) {
	const tableHeadings: string[] = ['ID', 'Company', 'Date Created', 'Total'];

	const { formatDate, formatCurrency } = useFormatters();

	const rowData: ComputedRef<string[][]> = computed(() =>
		webQuotesRaw.value.map((webQuote) => [webQuote.id, webQuote.customer, formatDate(webQuote.dateCreated), formatCurrency(webQuote.total)])
	);

	return {
		tableHeadings,
		rowData
	};
}
//#endregion

//#region Selection
function createSelectionModule(webQuotesRaw: Ref<WebQuote[]>) {
	const selectedWebQuoteId: Ref<string | null> = ref(null);

	const selectedWebQuote: ComputedRef<WebQuote | null> = computed(() => {
		if (!selectedWebQuoteId.value) return null;
		return webQuotesRaw.value.find((w) => w.id === selectedWebQuoteId.value) ?? null;
	});

	function selectWebQuote(id: string) {
		selectedWebQuoteId.value = id;
	}

	function clearSelectedWebQuote() {
		selectedWebQuoteId.value = null;
	}

	return {
		selectedWebQuoteId,
		selectedWebQuote,
		selectWebQuote,
		clearSelectedWebQuote
	};
}
//#endregion

//#region Data Access
async function fetchWebQuotesRaw() {
	const userStore = useUserStore();
	if (!userStore.user) {
		throw new Error(`Error: Failure reading user information`);
	}
	const idToken = await getIdToken(userStore.user, false);

	const envStore = useApiEnvStore();
	const response = await fetch(`${envStore.apiBaseUrl}/ListAllOpenQteedyQuotes`, {
		method: 'GET',
		headers: { Authorization: `Bearer ${idToken}` }
	});
	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response.status}`);
	}
	const webQuotes = (await response.json()) as WebQuote[];

	return webQuotes;
}
//#endregion

//#region Fetch
function createDataAccessModule(webQuotesList: ReturnType<typeof createListModule>, webQuoteStatus: ReturnType<typeof createStatusModule>) {
	async function fetchWebQuotes() {
		webQuotesList.webQuotesRaw.value = [];
		webQuoteStatus.isFetching.value = true;
		webQuoteStatus.webQuoteStatus.value = {
			statusSymbol: 'cached',
			statusText: 'Loading: Web Quotes'
		};

		try {
			const webQuotes = await fetchWebQuotesRaw();
			webQuotesList.webQuotesRaw.value = webQuotes;
			webQuoteStatus.resetWebQuoteStatus();
		} catch (e: any) {
			webQuotesList.webQuotesRaw.value = [];
			webQuoteStatus.webQuoteStatus.value = {
				statusSymbol: 'error',
				statusText: 'Failure: Unable to retrieve web quotes'
			};
			webQuoteStatus.isFetching.value = false;
			console.error(e);
		}
	}

	return {
		fetchWebQuotes
	};
}
//#endregion
